import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, RefreshCw, Scale, Wrench, Eraser } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatINR, formatNum } from "@/lib/api";
import { toast } from "sonner";

// Central Reconciliation — the single view that proves the three books
// (Supplier, Karigar, Product Inventory) agree with their underlying ledgers.
// Any row flagged as `mismatch` is a real issue ops needs to fix.
export function ReconciliationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [onlyMismatches, setOnlyMismatches] = useState(false);
  // Legacy Ledger Purge — one-click clean slate for pre-itemization ledger rows.
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const openPurge = async () => {
    try {
      const { data: p } = await api.get("/admin/reports/legacy-ledger-purge/preview");
      setPurgePreview(p);
      setPurgeOpen(true);
    } catch (_e) { toast.error("Could not preview the legacy ledger purge."); }
  };
  const runPurge = async () => {
    setPurging(true);
    try {
      const { data: r } = await api.post("/admin/reports/legacy-ledger-purge");
      toast.success(`Legacy ledger purged — removed ${r.removed_rows} old row${r.removed_rows === 1 ? "" : "s"}, rebuilt ${r.rebuilt_in_rows} piece-linked entr${r.rebuilt_in_rows === 1 ? "y" : "ies"}, re-synced ${r.resync?.changed || 0} stock count${r.resync?.changed === 1 ? "" : "s"}.`);
      setPurgeOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Legacy ledger purge failed.");
    } finally { setPurging(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/reports/reconciliation", { params: { only_mismatches: onlyMismatches } });
      setData(d);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyMismatches]);

  const summary = data?.summary || { supplier_mismatches: 0, karigar_mismatches: 0, product_mismatches: 0 };
  const totalMismatches = summary.supplier_mismatches + summary.karigar_mismatches + summary.product_mismatches;

  const runFifoResync = async () => {
    setResyncing(true);
    try {
      const { data: r } = await api.post("/admin/reports/stock-resync");
      const changed = r.resync?.changed || 0;
      const legacy = r.legacy?.migrated || 0;
      toast.success(
        changed > 0 || legacy > 0
          ? `Stock Audit complete — ${changed} SKU count${changed === 1 ? "" : "s"} re-synced to itemized pieces${legacy ? `, ${legacy} legacy piece(s) linked to an Opening bill` : ""}.`
          : "Stock Audit complete — every SKU count matches its itemized pieces.",
      );
      await load();
    } catch (_e) {
      toast.error("Stock Audit failed — please try again or check the backend logs.");
    } finally {
      setResyncing(false);
    }
  };

  const SectionCard = ({ title, count, mismatches, children }) => (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-[hsl(var(--primary))]" />
          <h3 className="font-medium text-sm">{title}</h3>
          <Badge variant="secondary">{count}</Badge>
        </div>
        {mismatches > 0 ? (
          <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9] gap-1"><AlertTriangle className="h-3 w-3" /> {mismatches} mismatch{mismatches === 1 ? "" : "es"}</Badge>
        ) : (
          <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7] gap-1"><CheckCircle2 className="h-3 w-3" /> All balanced</Badge>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">{children}</div>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="reconciliation-tab">
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium">Central Reconciliation</div>
          <p className="text-xs text-muted-foreground">Proves that Supplier, Karigar and Product balances match their underlying ledgers. Any mismatch is a book that needs to be reconciled.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch data-testid="only-mismatches-switch" checked={onlyMismatches} onCheckedChange={setOnlyMismatches} />
            <span>Only mismatches</span>
          </div>
          <Button data-testid="reconciliation-refresh-button" variant="outline" size="sm" className="gap-1" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </Card>

      <Card className={`p-4 border-2 ${totalMismatches ? "border-[#E7B9B9] bg-[#FBE7E7]" : "border-[#BFDCC7] bg-[#E7F3EA]"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {totalMismatches ? <AlertTriangle className="h-5 w-5 text-[#7A1F1F]" /> : <CheckCircle2 className="h-5 w-5 text-[#1F5B2E]" />}
            <div>
              <div className="text-sm font-medium">{totalMismatches ? `${totalMismatches} mismatch${totalMismatches === 1 ? "" : "es"} across all books` : "All books balanced with their ledgers"}</div>
              <div className="text-xs text-muted-foreground">
                Suppliers: {summary.supplier_mismatches} · Karigars: {summary.karigar_mismatches} · Products: {summary.product_mismatches}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="legacy-purge-button"
            variant="outline"
            size="sm"
            className="gap-1.5 bg-card"
            onClick={openPurge}
            disabled={purging}
            title="Wipe every pre-itemization ledger row (no piece link) and rebuild the ledger from the piece table so Reconciliation starts spotless."
          >
            <Eraser className="h-3.5 w-3.5" />
            Purge Legacy Ledger
          </Button>
          <Button
            data-testid="fifo-resync-button"
            variant="outline"
            size="sm"
            className="gap-1.5 bg-card"
            onClick={runFifoResync}
            disabled={resyncing}
            title="Re-syncs every SKU's stock count to its itemized in-stock pieces and audits the piece table against the movement ledger."
          >
            <Wrench className={`h-3.5 w-3.5 ${resyncing ? "animate-spin" : ""}`} />
            {resyncing ? "Running Stock Audit…" : "Run Stock Audit & Re-Sync"}
          </Button>
          </div>
        </div>
      </Card>

      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent data-testid="legacy-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Purge legacy ledger rows?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>This removes every inventory ledger row written <span className="font-medium text-foreground">before Source-Linked Itemization</span> (rows with no piece link), then rebuilds one piece-linked entry per existing piece from its Master Bill so ledger = pieces = stock for every SKU.</p>
                {purgePreview && (
                  <div className="rounded-lg border border-border bg-[#F7F0E6] p-3 font-mono text-xs space-y-1" data-testid="legacy-purge-preview">
                    <div>Legacy rows to remove: <span className="text-foreground font-medium">{purgePreview.legacy_rows}</span></div>
                    <div className="text-[11px]">{Object.entries(purgePreview.legacy_by_type || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</div>
                    <div>Piece-linked rows kept: <span className="text-foreground font-medium">{purgePreview.piece_linked_rows_kept}</span></div>
                    <div>Pieces to rebuild: <span className="text-foreground font-medium">{purgePreview.pieces_to_rebuild}</span>{purgePreview.pieces_to_rebuild_sold ? ` (${purgePreview.pieces_to_rebuild_sold} sold)` : ""}</div>
                  </div>
                )}
                <p>Piece-linked rows, pieces, bills, invoices and partner ledgers are <span className="font-medium text-foreground">not</span> touched. The action is audit-logged and safe to repeat.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-legacy-purge-button" onClick={(e) => { e.preventDefault(); runPurge(); }} disabled={purging || (purgePreview && purgePreview.legacy_rows === 0 && purgePreview.pieces_to_rebuild === 0)} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
              {purging ? "Purging…" : purgePreview?.legacy_rows === 0 && purgePreview?.pieces_to_rebuild === 0 ? "Nothing to purge" : "Purge & Rebuild"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SectionCard title="Suppliers — Outstanding vs Ledger" count={data?.suppliers?.length || 0} mismatches={summary.supplier_mismatches}>
        <Table data-testid="reconciliation-suppliers-table">
          <TableHeader>
            <TableRow className="bg-[#F4EBDD]">
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Computed (₹)</TableHead>
              <TableHead className="text-right">Ledger (₹)</TableHead>
              <TableHead className="text-right">Diff (₹)</TableHead>
              <TableHead className="text-right">Computed Gold</TableHead>
              <TableHead className="text-right">Ledger Gold</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.suppliers || []).map((r) => (
              <TableRow key={r.supplier_id} className={r.mismatch ? "bg-[#FBE7E7]" : ""}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right font-mono">{formatINR(r.computed_inr)}</TableCell>
                <TableCell className="text-right font-mono">{formatINR(r.ledger_inr)}</TableCell>
                <TableCell className={`text-right font-mono ${Math.abs(r.diff_inr) > 0.5 ? "text-destructive" : ""}`}>{formatINR(r.diff_inr)}</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.computed_gold)}g</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.ledger_gold)}g</TableCell>
                <TableCell className="text-right">{r.mismatch ? <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">Mismatch</Badge> : <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">OK</Badge>}</TableCell>
              </TableRow>
            ))}
            {(!data?.suppliers || data.suppliers.length === 0) && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No supplier data.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>

      <SectionCard title="Karigars — Outstanding vs Ledger" count={data?.karigars?.length || 0} mismatches={summary.karigar_mismatches}>
        <Table data-testid="reconciliation-karigars-table">
          <TableHeader>
            <TableRow className="bg-[#F4EBDD]">
              <TableHead>Karigar</TableHead>
              <TableHead className="text-right">Computed (₹)</TableHead>
              <TableHead className="text-right">Ledger (₹)</TableHead>
              <TableHead className="text-right">Opening Gold</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.karigars || []).map((r) => (
              <TableRow key={r.karigar_id} className={r.mismatch ? "bg-[#FBE7E7]" : ""}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right font-mono">{formatINR(r.computed_inr)}</TableCell>
                <TableCell className="text-right font-mono">{formatINR(r.ledger_inr)}</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.opening_gold_g)}g</TableCell>
                <TableCell className="text-right">{r.mismatch ? <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">Mismatch</Badge> : <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">OK</Badge>}</TableCell>
              </TableRow>
            ))}
            {(!data?.karigars || data.karigars.length === 0) && (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No karigar data.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>

      <SectionCard title="Products — Stock Cache vs Inventory Ledger" count={data?.products?.length || 0} mismatches={summary.product_mismatches}>
        <Table data-testid="reconciliation-products-table">
          <TableHeader>
            <TableRow className="bg-[#F4EBDD]">
              <TableHead>SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead className="text-right">Cache</TableHead>
              <TableHead className="text-right">Ledger</TableHead>
              <TableHead className="text-right">Pieces (in-stock)</TableHead>
              <TableHead className="text-right">Diff</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.products || []).map((r) => (
              <TableRow key={r.product_id} className={r.mismatch ? "bg-[#FBE7E7]" : ""}>
                <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-right font-mono">{r.stock_cache}</TableCell>
                <TableCell className="text-right font-mono">{r.ledger_sum}</TableCell>
                <TableCell className="text-right font-mono">{r.pieces_in_stock}</TableCell>
                <TableCell className={`text-right font-mono ${r.diff !== 0 ? "text-destructive" : ""}`}>{r.diff}</TableCell>
                <TableCell className="text-right">{r.mismatch ? <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">Mismatch</Badge> : <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">OK</Badge>}</TableCell>
              </TableRow>
            ))}
            {(!data?.products || data.products.length === 0) && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No product data.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>
    </div>
  );
}
